import useThrottledCallback from 'beautiful-react-hooks/useThrottledCallback';
import useWindowScroll from 'beautiful-react-hooks/useWindowScroll';
import clsx from 'clsx';
import { memo, useEffect, useRef, useState } from 'react';

import VideoLabels from '../VideoLabels';

import { useDrawVideoPlayerOptions } from '@apps/draw-centre/hooks';
import { useDrawCentreOptions, useDrawCentreStore } from '@apps/draw-centre/store';
import VideoPlayer from '@apps/videoplayer/app';
import { useWindowSize } from '@shared/hooks/useWindowSize';
import SlotIncluder from '@shared/SlotIncluder';

import * as styles from './styles.module.scss';

const DrawVideo = () => {
  const {
    options: { videoPromoLibrary },
  } = useDrawCentreOptions();

  const { videoPlayerOptions } = useDrawVideoPlayerOptions();

  const setVideoHeight = useDrawCentreStore((state) => state.setVideoHeight);
  const videoHeight = useDrawCentreStore((state) => state.videoHeight);

  const { isMobile, isTablet } = useWindowSize();
  const onScroll = useWindowScroll();

  const [isFixed, setIsFixed] = useState(false);

  const videoRef = useRef<HTMLDivElement>(null);

  const videoWrapperRef = useRef<HTMLDivElement>(null);

  const isSmallScreen = isMobile || isTablet;

  const handleScroll = useThrottledCallback(
    () => {
      if (!videoRef.current || !videoWrapperRef.current) return;

      const { top: videoWrapperTop } = videoWrapperRef.current.getBoundingClientRect();
      const stickyHeight = Array.from(document.querySelectorAll('.navigation--sticky, .affix')).reduce(
        (acc, elem) => acc + elem.clientHeight,
        0,
      );

      const videoPresent = videoWrapperRef.current.clientHeight > 0;
      if (videoPresent) {
        const videoFixed = isSmallScreen && videoWrapperTop < stickyHeight;

        // Set the height of the video wrapper if fixed; remove it otherwise
        if (videoFixed) {
          videoWrapperRef.current.style.setProperty('height', `${videoWrapperRef.current.clientHeight}px`);
          setVideoHeight(videoRef.current.clientHeight);
        } else {
          videoWrapperRef.current.style.removeProperty('height');
          setVideoHeight(0);
        }

        setIsFixed(videoFixed);
        videoRef.current.style.setProperty('top', `${stickyHeight}px`);
      }
    },
    [isMobile, isTablet],
    100,
  );

  onScroll(handleScroll);

  useEffect(() => {
    isSmallScreen && !videoHeight && setVideoHeight(videoRef.current.clientHeight);
  }, [isSmallScreen, setVideoHeight, videoHeight]);

  return (
    <div ref={videoWrapperRef} className={styles.stickyVideo}>
      <div ref={videoRef} className={clsx(isFixed && styles.fixedVideo)}>
        <VideoPlayer options={videoPlayerOptions} />
      </div>
      <VideoLabels />
      {isSmallScreen && <SlotIncluder className='pk-mt--m' path={`${videoPromoLibrary}?gpt_type=leaderboard-small`} />}
    </div>
  );
};

export default memo(DrawVideo);
